/**
 * Navigation Guards
 *
 * Route guards for authentication flow
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { saveRedirectUri, getRedirectUri } from '@/utils';
import { AuthEventReporter } from '@/services/telemetry';

/**
 * Auth guard - checks if user is authenticated
 * Redirects to login if required auth is missing
 */
export function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore();

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    if (!authStore.isAuthenticated) {
      // Completely silent today. The Pinia store is not persisted, so ANY refresh on
      // /login/mfa-setup or /login/success lands here and throws the user back to the email
      // step with no explanation — indistinguishable, from the user's side, from "login broken".
      //
      // This event reports the bounce; persisting the store to fix the cause is a separate change.
      AuthEventReporter.report({
        stage: 'ROUTE_GUARD',
        step: 'guards.authGuard.blocked',
        errorCode: 'RouteGuardBlocked',
        errorSource: 'CLIENT',
        errorDetail: `blocked route: ${String(to.name ?? to.path)}`,
        wasUserVisible: false,
      });
      // Redirect to email input if not authenticated
      next({ name: 'email-input' });
      return;
    }
  }

  // Check if route requires email to be set
  if (to.meta.requiresEmail) {
    if (!authStore.email) {
      // Redirect to email input if email not set
      next({ name: 'email-input' });
      return;
    }
  }

  next();
}

/**
 * Redirect URI interceptor
 * Parses and saves redirect_uri from query params
 */
export function redirectUriInterceptor(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const authStore = useAuthStore();

  // Check for redirect_uri in query params
  const redirectUri = to.query.redirect_uri as string | undefined;

  if (redirectUri) {
    // Save to localStorage
    saveRedirectUri(redirectUri);

    // Save to store
    authStore.setReferrerUrl(redirectUri);

    // Remove from URL (clean up)
    const query = { ...to.query };
    delete query.redirect_uri;

    next({
      ...to,
      query,
      replace: true,
    });
    return;
  }

  // Check if we have a saved redirect URI
  const savedRedirectUri = getRedirectUri();
  if (savedRedirectUri && !authStore.referrerUrl) {
    authStore.setReferrerUrl(savedRedirectUri);
  }

  next();
}

/**
 * Step guard (optional)
 * Prevents users from skipping steps in the auth flow
 * Currently disabled - we allow direct navigation
 */
export function stepGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  // For now, allow all navigation
  // This can be enhanced later if needed
  next();
}

/**
 * Page title guard
 * Updates document title based on route meta
 */
export function pageTitleGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): void {
  const title = to.meta.title as string | undefined;

  if (title) {
    document.title = `${title} | Bono Auth`;
  } else {
    document.title = 'Bono Auth';
  }

  next();
}

/**
 * Combine all guards
 * Apply guards in order
 */
export function setupGuards(router: any): void {
  // Apply guards in order
  router.beforeEach(redirectUriInterceptor);
  router.beforeEach(authGuard);
  router.beforeEach(pageTitleGuard);
}